import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  problemTreeCauseStatus,
  type ProblemTreeCause,
  type ProblemTreeCauseStatus,
  type ProblemTreeNode,
  type ProblemTreeParams,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import {
  CauseStatusBadge,
  CauseStatusMenu,
  RULED_OUT_FADE,
} from "./CauseStatus.js";

/**
 * Problem Tree — Specify, singleton. Reference-style tree preview with
 * foliage/effects, problem trunk, and root-cause stickies.
 *
 * Ported from ui-elements/components/solves/workspace/specify/ProblemTreeCard.tsx
 * and adapted to the current SQL widget schema.
 */
export function ProblemTreeWidget({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ProblemTreeParams>): ReactElement {
  const effects = useMemo(
    () => visibleNodes(data.effects, "Symptom"),
    [data.effects],
  );
  // Placeholder causes show the default status, but remain read-only until
  // authored so changing a badge cannot persist invented causes.
  const causes = useMemo(
    () => (data.causes.length > 0 ? [...data.causes] : null),
    [data.causes],
  );
  const problem = data.problem.trim() || "Problem statement";

  return (
    <ProblemTreePreview
      effects={effects}
      problem={problem}
      causes={causes ?? visibleNodes([], "Cause")}
      onCauseStatusChange={
        readOnly || causes === null
          ? undefined
          : (id, status) =>
              onChange({
                ...data,
                causes: data.causes.map((cause) =>
                  cause.id === id ? { ...cause, status } : cause,
                ),
              })
      }
    />
  );
}

const STICKY_W = 96;
const STICKY_H = 96;
const TRUNK_W = 220;
const TRUNK_H = 64;

const SLOT_PITCH_X = 130;
const SLOT_PITCH_Y = 110;

const BUMP_W = 180;
const BUMP_H = 150;
const BUMP_RX = 70;

const STEM_W = 60;
const TRUNK_GAP_TOP = 28;
const TRUNK_GAP_BOTTOM = 24;

const TRUNK_X = -TRUNK_W / 2;
const TRUNK_Y = -TRUNK_H / 2;
const TRUNK_TOP_Y = TRUNK_Y;
const TRUNK_BOTTOM_Y = TRUNK_Y + TRUNK_H;
const TRUNK_CENTER_X = 0;

interface SlotInfo {
  readonly x: number;
  readonly y: number;
}

interface CenterPoint {
  readonly id: string;
  readonly cx: number;
  readonly cy: number;
}

interface TreePieces {
  readonly effects: ReadonlyArray<CenterPoint>;
  readonly causes: ReadonlyArray<CenterPoint>;
  readonly trunkCenter: {
    readonly cx: number;
    readonly topY: number;
    readonly bottomY: number;
  };
}

function ProblemTreePreview({
  effects,
  problem,
  causes,
  onCauseStatusChange,
}: {
  effects: ReadonlyArray<ProblemTreeNode>;
  problem: string;
  causes: ReadonlyArray<ProblemTreeCause>;
  onCauseStatusChange?:
    | ((id: string, status: ProblemTreeCauseStatus) => void)
    | undefined;
}): ReactElement {
  const pieces = useMemo<TreePieces>(
    () => ({
      effects: effects.map((n, i) => {
        const slot = effectSlot(i, effects.length);
        return {
          id: n.id,
          cx: slot.x + STICKY_W / 2,
          cy: slot.y + STICKY_H / 2,
        };
      }),
      causes: causes.map((n, i) => {
        const slot = causeSlot(i, causes.length);
        return {
          id: n.id,
          cx: slot.x + STICKY_W / 2,
          cy: slot.y + STICKY_H / 2,
        };
      }),
      trunkCenter: {
        cx: TRUNK_CENTER_X,
        topY: TRUNK_TOP_Y,
        bottomY: TRUNK_BOTTOM_Y,
      },
    }),
    [causes, effects],
  );

  const bounds = useMemo(
    () => stageBounds(effects.length, causes.length),
    [causes.length, effects.length],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let raf = 0;
    const updateScale = (): void => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const w = el.clientWidth;
        const h = el.clientHeight;
        if (w > 0 && h > 0) {
          setScale(Math.min(w / bounds.width, h / bounds.height));
        }
      });
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [bounds.height, bounds.width]);

  const scaledW = bounds.width * scale;
  const scaledH = bounds.height * scale;

  /* Kept hand-built (design-system adoption): the preview is a scaled
     illustration — sticky paper, the trunk and SVG branches placed in tree
     space — not a control or a card, so no design-system part draws it. */
  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 rounded-md border border-pb-border bg-pb-card-elevated/40 overflow-hidden flex items-center justify-center"
    >
      <div
        style={{
          width: scaledW,
          height: scaledH,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: bounds.width,
            height: bounds.height,
            transform: `translate(${-bounds.minX * scale}px, ${-bounds.minY * scale}px) scale(${scale})`,
            transformOrigin: "0 0",
          }}
        >
          <TreeIllustration pieces={pieces} />

          {effects.map((node, index) => {
            const slot = effectSlot(index, effects.length);
            return (
              <Sticky
                key={node.id}
                node={node}
                tone="effect"
                x={slot.x}
                y={slot.y}
              />
            );
          })}

          <div
            className="absolute bg-pb-illustration-problem text-pb-illustration-problem-text border border-pb-illustration-problem-border/70 shadow-md flex items-center justify-center font-bold text-caption px-2 text-center overflow-hidden"
            style={{
              left: TRUNK_X,
              top: TRUNK_Y,
              width: TRUNK_W,
              height: TRUNK_H,
            }}
            title={problem}
          >
            {problem}
          </div>

          {causes.map((node, index) => {
            const slot = causeSlot(index, causes.length);
            const status = problemTreeCauseStatus(node);
            return (
              <Sticky
                key={node.id}
                node={node}
                tone="cause"
                x={slot.x}
                y={slot.y}
                status={status}
                onStatusChange={onCauseStatusChange}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Sticky({
  node,
  tone,
  x,
  y,
  status,
  onStatusChange,
}: {
  node: ProblemTreeNode;
  tone: "effect" | "cause";
  x: number;
  y: number;
  status?: ProblemTreeCauseStatus | undefined;
  onStatusChange?:
    | ((id: string, status: ProblemTreeCauseStatus) => void)
    | undefined;
}): ReactElement {
  const className =
    tone === "effect"
      ? "bg-pb-illustration-symptom text-pb-illustration-symptom-text"
      : "bg-pb-illustration-cause text-pb-illustration-cause-text";

  return (
    <div
      className={`absolute ${className} flex flex-col items-center justify-center gap-1 text-dense-3 leading-tight font-medium px-1 py-1 text-center overflow-hidden ${
        status === "ruled_out" ? RULED_OUT_FADE : ""
      }`}
      style={{
        left: x,
        top: y,
        width: STICKY_W,
        height: STICKY_H,
        transform: `rotate(${stickyRotation(node.id)}deg)`,
      }}
      title={node.text}
    >
      <span className="min-w-0 overflow-hidden">{node.text}</span>
      {status ? (
        onStatusChange ? (
          <CauseStatusMenu
            status={status}
            causeLabel={node.text}
            onChange={(next) => onStatusChange(node.id, next)}
          />
        ) : (
          <CauseStatusBadge status={status} className="shrink-0" />
        )
      ) : null}
    </div>
  );
}

function TreeIllustration({ pieces }: { pieces: TreePieces }): ReactElement {
  const { effects, causes, trunkCenter } = pieces;
  const allX: number[] = [
    trunkCenter.cx - TRUNK_W / 2,
    trunkCenter.cx + TRUNK_W / 2,
  ];
  const allY: number[] = [trunkCenter.topY, trunkCenter.bottomY];

  effects.forEach((p) => {
    allX.push(p.cx - BUMP_W / 2, p.cx + BUMP_W / 2);
    allY.push(p.cy - BUMP_H / 2, p.cy + BUMP_H / 2);
  });
  causes.forEach((p) => {
    allX.push(p.cx - 60, p.cx + 60);
    allY.push(p.cy - 30, p.cy + 60);
  });

  const pad = 30;
  const minX = Math.min(...allX) - pad;
  const maxX = Math.max(...allX) + pad;
  const minY = Math.min(...allY) - pad;
  const maxY = Math.max(...allY) + pad;
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);

  const stemX = trunkCenter.cx - STEM_W / 2;
  const stemTop =
    effects.length > 0
      ? Math.max(...effects.map((p) => p.cy + BUMP_H / 2)) - 30
      : trunkCenter.topY - 8;
  const stemBottom = trunkCenter.topY + 8;
  const stemH = Math.max(0, stemBottom - stemTop);

  return (
    <svg
      width={w}
      height={h}
      viewBox={`${minX} ${minY} ${w} ${h}`}
      style={{
        position: "absolute",
        left: minX,
        top: minY,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      <defs>
        <filter id="pt-goo" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
          <feColorMatrix
            mode="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10"
          />
        </filter>
      </defs>

      {causes.map((p) => {
        const sx = trunkCenter.cx;
        const sy = trunkCenter.bottomY;
        const ex = p.cx;
        const ey = p.cy;
        const dx = ex - sx;
        const dy = Math.max(40, ey - sy);
        const jitter = stickyJitter(p.id, 7) * 40;
        const c1x = sx + dx * 0.15 + jitter;
        const c1y = sy + dy * 0.45;
        const c2x = ex - dx * 0.1 + jitter * 0.5;
        const c2y = ey - dy * 0.25;
        return (
          <g key={`root-${p.id}`}>
            <path
              d={`M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey + 10}`}
              fill="none"
              strokeWidth={5.5}
              strokeLinecap="round"
              className="stroke-pb-illustration-root"
            />
            <ellipse
              cx={ex}
              cy={ey + STICKY_H / 2 + 4}
              rx={36}
              ry={14}
              className="fill-pb-illustration-root/15"
            />
          </g>
        );
      })}

      <rect
        x={trunkCenter.cx - 40}
        y={trunkCenter.topY - 100}
        width={80}
        height={TRUNK_H + 200}
        rx={10}
        ry={10}
        className="fill-pb-illustration-trunk"
      />

      {(effects.length > 0 || stemH > 0) && (
        <g filter="url(#pt-goo)" className="fill-pb-illustration-foliage">
          {effects.map((p) => (
            <rect
              key={`foliage-${p.id}`}
              x={p.cx - BUMP_W / 2}
              y={p.cy - BUMP_H / 2}
              width={BUMP_W}
              height={BUMP_H}
              rx={BUMP_RX}
              ry={BUMP_RX}
            />
          ))}
          {stemH > 0 ? (
            <rect
              x={stemX - 2}
              y={stemTop}
              width={STEM_W + 4}
              height={stemH}
              rx={20}
              ry={20}
            />
          ) : null}
        </g>
      )}
    </svg>
  );
}

/**
 * The tree's NATURAL drawing box for a given node count.
 *
 * Exported for the report export, which reserves a figure box on the page from
 * it: the preview scales to `min(w/width, h/height)`, so a box with a different
 * aspect letterboxes the diagram and shrinks its labels for nothing. Pure
 * geometry — no rendering behaviour depends on it being exported.
 */
export function problemTreeStageBounds(
  effectCount: number,
  causeCount: number,
): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  return stageBounds(effectCount, causeCount);
}

function stageBounds(
  effectCount: number,
  causeCount: number,
): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  const xs: number[] = [TRUNK_X, TRUNK_X + TRUNK_W];
  const ys: number[] = [TRUNK_Y, TRUNK_Y + TRUNK_H];

  Array.from({ length: effectCount }).forEach((_, i) => {
    const slot = effectSlot(i, effectCount);
    xs.push(slot.x - 50, slot.x + STICKY_W + 50);
    ys.push(slot.y - 50, slot.y + STICKY_H + 30);
  });
  Array.from({ length: causeCount }).forEach((_, i) => {
    const slot = causeSlot(i, causeCount);
    xs.push(slot.x - 30, slot.x + STICKY_W + 30);
    ys.push(slot.y - 20, slot.y + STICKY_H + 40);
  });

  const pad = 24;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  const maxX = Math.max(...xs) + pad;
  const maxY = Math.max(...ys) + pad;
  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function visibleNodes(
  nodes: ReadonlyArray<ProblemTreeNode>,
  label: "Symptom" | "Cause",
): ProblemTreeNode[] {
  if (nodes.length > 0) return [...nodes];
  return Array.from({ length: 3 }, (_, i) => ({
    id: `${label.toLowerCase()}-${i + 1}`,
    text: `${label} ${i + 1}`,
  }));
}

function distributeRows(count: number, maxPerRow = 4): number[] {
  if (count <= 0) return [];
  const rows = Math.min(Math.ceil(count / maxPerRow), 4);
  const base = Math.floor(count / rows);
  const extras = count - base * rows;
  return Array.from(
    { length: rows },
    (_, i) => base + (i >= rows - extras ? 1 : 0),
  );
}

const EFFECT_ROW_TABLE: ReadonlyArray<readonly number[]> = [
  [],
  [1],
  [2],
  [1, 2],
  [1, 3],
  [2, 3],
  [1, 2, 3],
  [2, 2, 3],
  [2, 3, 3],
  [1, 3, 5],
  [1, 2, 3, 4],
  [1, 3, 3, 4],
  [2, 2, 4, 4],
  [1, 3, 4, 5],
  [2, 3, 4, 5],
  [3, 3, 4, 5],
  [1, 3, 5, 7],
];

function distributeEffectRows(count: number): number[] {
  if (count <= 0) return [];
  const preset = EFFECT_ROW_TABLE[count];
  if (preset) return [...preset];
  const rowCount = 4;
  const base = Math.floor(count / rowCount);
  const extras = count - base * rowCount;
  return Array.from(
    { length: rowCount },
    (_, i) => base + (i >= rowCount - extras ? 1 : 0),
  );
}

function effectSlot(index: number, total: number): SlotInfo {
  const rows = distributeEffectRows(total);
  let acc = 0;
  let rowIdx = 0;
  let inRow = 0;
  for (let r = 0; r < rows.length; r += 1) {
    if (index < acc + rows[r]!) {
      rowIdx = r;
      inRow = index - acc;
      break;
    }
    acc += rows[r]!;
  }
  const perRow = rows[rowIdx] ?? 1;
  const rowWidth = perRow * SLOT_PITCH_X;
  const startX = TRUNK_CENTER_X - rowWidth / 2 + (SLOT_PITCH_X - STICKY_W) / 2;
  const distanceFromTrunk = (rows.length - rowIdx) * SLOT_PITCH_Y;
  const slotTopY = TRUNK_TOP_Y - TRUNK_GAP_TOP - distanceFromTrunk;
  return {
    x: startX + inRow * SLOT_PITCH_X,
    y: slotTopY + (SLOT_PITCH_Y - STICKY_H) / 2,
  };
}

function causeSlot(index: number, total: number): SlotInfo {
  const rows = distributeRows(total);
  let acc = 0;
  let rowIdx = 0;
  let inRow = 0;
  for (let r = 0; r < rows.length; r += 1) {
    if (index < acc + rows[r]!) {
      rowIdx = r;
      inRow = index - acc;
      break;
    }
    acc += rows[r]!;
  }
  const perRow = rows[rowIdx] ?? 1;
  const rowWidth = perRow * SLOT_PITCH_X;
  const startX = TRUNK_CENTER_X - rowWidth / 2 + (SLOT_PITCH_X - STICKY_W) / 2;
  const slotTopY = TRUNK_BOTTOM_Y + TRUNK_GAP_BOTTOM + rowIdx * SLOT_PITCH_Y;
  return {
    x: startX + inRow * SLOT_PITCH_X,
    y: slotTopY + (SLOT_PITCH_Y - STICKY_H) / 2,
  };
}

function stickyRotation(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 7) - 3;
}

function stickyJitter(id: string, salt: number): number {
  let hash = salt;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 33 + id.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 100) / 50 - 1;
}
